import React, {useRef, useState} from "react";
import * as moment from 'moment-timezone';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
    Box,
    Button,
    ButtonGroup,
    Popper,
    Grow,
    Paper,
    ClickAwayListener,
    MenuItem,
    MenuList,
} from "@mui/material";

import SelectedServiceTypeDetails from "../SelectedServiceTypeDetails";
import SelectedBookableServices from "../SelectedBookableServices";
import {SECONDS_INTO_MINUTES, MOMENT_UNIT_MINUTES} from "../../domain/ShiftPlanConstants";
import {BOOKABLE_STATUS} from "../../domain/BookableStatus";

export default function ServiceTypeMultiSelect({
    selectedServiceTypes,
    setSelectedServiceTypes,
    availableServiceTypes,
    bookableServiceTypes,
    serviceTypeSelectionOptions,
    handleBookableStatusChange,
    setBookableStatuses,
    bookableStatuses,
    handleBookableServiceTypesTimestampUpdate,
    bookableServiceTypesTimestamps
}) {

    const [bookableServicePopperOpen, setBookableServicePopperOpen] = useState(false);

    const anchorRef = useRef(null);

    const bookableServicesDescriptions = availableServiceTypes.map(type => type.description);

    const handleToggle = () => setBookableServicePopperOpen((prevOpen) => !prevOpen);

    const handleStartTimeChange = (uniqueBookableServiceRowId, newStartTime) => {
        handleBookableServiceTypesTimestampUpdate(prev => {
            return {
                ...prev,
                [uniqueBookableServiceRowId]: {
                    ...prev[uniqueBookableServiceRowId],
                    startTime: newStartTime
                }
            };
        });
    };

    const handleEndTimeChange = (uniqueBookableServiceRowId, newEndTime) => {
        const startTime = bookableServiceTypesTimestamps[uniqueBookableServiceRowId]?.startTime;
        const serviceType = selectedServiceTypes.find(type => type.uniqueBookableServiceRowId === uniqueBookableServiceRowId);
        const maxDurationInMinutes = (serviceType.timeslotLengthInSeconds * serviceType.maxPeriodsInShift) / SECONDS_INTO_MINUTES;
        const maxEndTime = moment(startTime).add(maxDurationInMinutes, MOMENT_UNIT_MINUTES).toDate();
        let adjustedEndTime = moment(newEndTime)

        if (moment(adjustedEndTime).isAfter(maxEndTime)) {
            adjustedEndTime = maxEndTime;
        }

        handleBookableServiceTypesTimestampUpdate(prev => ({
            ...prev,
            [uniqueBookableServiceRowId]: {
                ...prev[uniqueBookableServiceRowId],
                endTime: adjustedEndTime
            }
        }));
    };

    const removeServiceType = (uniqueBookableServiceRowId) => {
        handleBookableServiceTypesTimestampUpdate(prevServiceTypeTimes => {
            const updatedServiceTypeTimes = {...prevServiceTypeTimes};
            delete updatedServiceTypeTimes[uniqueBookableServiceRowId];
            return updatedServiceTypeTimes;
        });

        if (serviceTypeSelectionOptions.enableBookableServices) {
            setBookableStatuses(prevStatuses => {
                const updatedStatuses = {...prevStatuses};
                delete updatedStatuses[uniqueBookableServiceRowId];
                return updatedStatuses;
            });
        }
        setSelectedServiceTypes(prev => prev.filter(type => type.uniqueBookableServiceRowId !== uniqueBookableServiceRowId));
    };

    const getBookableService = (keyCode) => {
        return availableServiceTypes.find(type => type.keycode === keyCode);
    };

    const getCurrentBookableService = (keyCode) => {
        return bookableServiceTypes?.find(type => type.keycode === keyCode);
    };

    const generateUniqueRowId = (keyCode) => {
        return `${keyCode}-${Date.now()}`;
    };

    const createUpdatedServiceType = (requestedService, currentService, uniqueId) => {
        return {
            ...requestedService,
            id: currentService?.id,
            keycode: requestedService.keycode,
            uniqueBookableServiceRowId: uniqueId
        };
    };

    const addServiceTypeToSelectedList = (updatedCurrentServiceType, uniqueBookableServiceRowId) => {
        setSelectedServiceTypes(previouslySelectedServiceTypes => {
            if (serviceTypeSelectionOptions.enableBookableServices) {
                handleBookableStatusChange(updatedCurrentServiceType, uniqueBookableServiceRowId, BOOKABLE_STATUS.Bookable);
            }
            return [...previouslySelectedServiceTypes, updatedCurrentServiceType];
        });
    };

    const handleUpdatedServiceType = (updatedServiceTypeKeyCode) => {
        const bookableServiceRequestedByUser = getBookableService(updatedServiceTypeKeyCode);
        const currentBookableServiceType = getCurrentBookableService(updatedServiceTypeKeyCode);
        const uniqueBookableServiceRowId = generateUniqueRowId(updatedServiceTypeKeyCode);
        const updatedCurrentServiceType = createUpdatedServiceType(
            bookableServiceRequestedByUser,
            currentBookableServiceType,
            uniqueBookableServiceRowId
        );

        addServiceTypeToSelectedList(updatedCurrentServiceType, uniqueBookableServiceRowId);
        setBookableServicePopperOpen(false);
    };

    return <>

        <Box>
            {
              serviceTypeSelectionOptions.enableServiceTypeDetails
              && selectedServiceTypes?.slice()
              .map(serviceType =>
                <Box key={serviceType.uniqueBookableServiceRowId}>
                    <SelectedServiceTypeDetails
                      selectedServiceType={serviceType}
                      startTime={bookableServiceTypesTimestamps[serviceType.uniqueBookableServiceRowId]?.startTime}
                      endTime={bookableServiceTypesTimestamps[serviceType.uniqueBookableServiceRowId]?.endTime}
                      onStartTimeChange={time => handleStartTimeChange(serviceType.uniqueBookableServiceRowId, time)}
                      onEndTimeChange={time => handleEndTimeChange(serviceType.uniqueBookableServiceRowId, time)}
                      onRemoveService={() => removeServiceType(serviceType.uniqueBookableServiceRowId)}/>
                </Box>
              )
            }

            {
              serviceTypeSelectionOptions.enableBookableServices
              && selectedServiceTypes?.slice()
              .map(serviceType =>
                <Box key={serviceType.uniqueBookableServiceRowId}>
                    <SelectedBookableServices
                      selectedServiceType={serviceType}
                      onStatusChange={status => handleBookableStatusChange(serviceType, serviceType.uniqueBookableServiceRowId, status)}
                      bookableStatus={bookableStatuses[serviceType.uniqueBookableServiceRowId] || BOOKABLE_STATUS.Bookable}
                      startTime={bookableServiceTypesTimestamps[serviceType.uniqueBookableServiceRowId]?.startTime}
                      endTime={bookableServiceTypesTimestamps[serviceType.uniqueBookableServiceRowId]?.endTime}
                      onStartTimeChange={time => handleStartTimeChange(serviceType.uniqueBookableServiceRowId, time)}
                      onEndTimeChange={time => handleEndTimeChange(serviceType.uniqueBookableServiceRowId, time)}
                      onRemoveService={() => removeServiceType(serviceType.uniqueBookableServiceRowId)}/>
                </Box>
              )
            }
        </Box>

        <ButtonGroup variant="contained" ref={anchorRef} aria-label="Service Type selection"  sx={{marginTop: '1rem'}}>
            <Button>Add</Button>
            <Button size="small" aria-haspopup="menu" onClick={handleToggle}>
                <ArrowDropDownIcon/>
            </Button>
        </ButtonGroup>

        <Popper sx={{zIndex: 2}} open={bookableServicePopperOpen} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
            {({TransitionProps, placement}) => (
                <Grow {...TransitionProps}
                style={{transformOrigin: placement === "bottom" ? "center top" : "center bottom"}}>
                    <Paper sx={{backgroundColor: "white"}}>
                        <ClickAwayListener onClickAway={() => setBookableServicePopperOpen(false)}>
                            <MenuList id="split-button-menu" autoFocusItem>
                                {bookableServicesDescriptions.map((option, index) => (
                                    <MenuItem key={option} onClick={() => handleUpdatedServiceType(availableServiceTypes[index].keycode)}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>

    </>;
}